import { api } from '../slices/api';

export interface Building {
	id: string;
	name: string;
	slug?: string;
	// When true (e.g. West Bend) non-bundle received material is auto-classified
	// into bins/slots/shelves instead of going to overflow.
	auto_assign_racks?: boolean;
	// Length (in inches) at/above which received material is treated as a bundle.
	// Serialized as a string by DRF's DecimalField; coerce with Number() before use.
	bundle_min_length?: number | string;
}

export interface BuildingPrinterSettings {
	printing_service_url: string;
	printer_name_delivery: string;
	printer_name_kitting: string;
	printer_name_cutting: string;
	printer_name_received: string;
	// Finishing / handling / cleaning tags. Blank falls back to the kitting
	// printer, which is what happens until the second printer is installed.
	printer_name_process_tags: string;
}

export interface BuildingWithPrinterSettings extends Building, BuildingPrinterSettings {}

export interface BuildingIntegrationSettings {
	id: string;
	building_id: string;
	building_name: string;
	building_slug: string;
	pb_proxy_url: string;
	kit_tracking_url: string;
	shop_floor_api_url: string;
	has_shop_floor_api_user: boolean;
	has_shop_floor_api_password: boolean;
	has_shop_floor_api_token: boolean;
	printing_service_url: string;
	printer_name_delivery: string;
	printer_name_kitting: string;
	printer_name_cutting: string;
	printer_name_received: string;
	printer_name_process_tags: string;
}

export type BuildingIntegrationSettingsUpdate = Partial<
	Omit<
		BuildingIntegrationSettings,
		| 'id'
		| 'building_id'
		| 'building_name'
		| 'building_slug'
		| 'has_shop_floor_api_user'
		| 'has_shop_floor_api_password'
		| 'has_shop_floor_api_token'
	>
> & {
	shop_floor_api_user?: string;
	shop_floor_api_password?: string;
	shop_floor_api_token?: string;
};

export interface LocationLabelRules {
	// "RACK|CODE" keys whose slot code repeats within the rack; those keep the shelf.
	ambiguous: string[];
}

const locationApi = api.injectEndpoints({
	endpoints: (builder) => ({
		getLocationLabelRules: builder.query<LocationLabelRules, void>({
			query: () => 'inventory/locations/label-rules/',
			// Rack / shelf / location edits in this tab refetch the rules at once.
			providesTags: ['Racks', 'Shelves', 'Locations'],
			keepUnusedDataFor: 60 * 60,
		}),
		getBuildings: builder.query<Building[], void>({
			query: () => 'inventory/buildings/',
		}),
		getBuildingsWithPrinterSettings: builder.query<BuildingWithPrinterSettings[], void>({
			query: () => 'inventory/buildings/',
			providesTags: ['Buildings'],
		}),
		updateBuildingPrinterSettings: builder.mutation<
			BuildingWithPrinterSettings,
			{ id: string } & BuildingPrinterSettings
		>({
			query: ({ id, ...body }) => ({
				url: `inventory/buildings/${id}/printer-settings/`,
				method: 'PATCH',
				body,
			}),
			invalidatesTags: ['Buildings'],
		}),
		getBuildingIntegrationSettings: builder.query<BuildingIntegrationSettings[], void>({
			query: () => 'inventory/building-integration-settings/',
			providesTags: ['BuildingIntegrationSettings'],
		}),
		updateBuildingIntegrationSettings: builder.mutation<
			BuildingIntegrationSettings,
			{ id: string } & BuildingIntegrationSettingsUpdate
		>({
			query: ({ id, ...body }) => ({
				url: `inventory/building-integration-settings/${id}/`,
				method: 'PATCH',
				body,
			}),
			invalidatesTags: ['BuildingIntegrationSettings', 'Buildings'],
		}),
	}),
});

export const {
	useGetLocationLabelRulesQuery,
	useGetBuildingsQuery,
	useGetBuildingsWithPrinterSettingsQuery,
	useUpdateBuildingPrinterSettingsMutation,
	useGetBuildingIntegrationSettingsQuery,
	useUpdateBuildingIntegrationSettingsMutation,
} = locationApi;
